import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { X, Save, Eye, EyeOff, ShieldCheck } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VISIBILITY_OPTIONS = [
  { value: "public", label: "Everyone", icon: Eye },
  { value: "coaches", label: "Coaches & Scouts Only", icon: ShieldCheck },
  { value: "private", label: "Only Me", icon: EyeOff },
];

function VisibilityToggle({ field, value, onChange }) {
  return (
    <div className="flex gap-1">
      {VISIBILITY_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(field, opt.value)}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition ${
              active ? "bg-blue-100 text-blue-700 font-medium" : "bg-gray-100 text-gray-400 hover:bg-gray-200"
            }`}
            title={opt.label}
            data-testid={`visibility-${field}-${opt.value}`}
          >
            <Icon size={11} />
            <span className="hidden sm:inline">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ProfileEditModal({ profile, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [visibility, setVisibility] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name || "",
        position_primary: profile.position_primary || "",
        position_secondary: profile.position_secondary || "",
        school: profile.school || "",
        location: profile.location || "",
        graduation_year: profile.graduation_year || "",
        height: profile.height || "",
        weight: profile.weight || "",
        bats: profile.bats || "",
        throws: profile.throws || "",
        gpa: profile.gpa || "",
        bio: profile.bio || "",
        highlight_video_url: profile.highlight_video_url || "",
      });
      setVisibility(profile.field_visibility || {
        school: "public",
        location: "public",
        height: "public",
        weight: "public",
        gpa: "coaches",
        bio: "public",
        metrics: "public",
        scouting_reports: "coaches",
        files: "coaches",
        highlight_video_url: "public",
      });
    }
  }, [profile]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleVisibility = (field, value) => {
    setVisibility((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/athlete/update-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...form, field_visibility: visibility }),
      });
      if (res.ok) {
        toast.success("Profile updated!");
        onSave(await res.json());
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Update failed");
      }
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const isVerified = (field) => {
    const vm = profile?.verified_metrics || {};
    const nm = profile?.nsb_grades || {};
    return vm[field] !== undefined || nm[field] !== undefined;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="profile-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">Edit Profile</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Full Name</Label>
              <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="edit-name" />
            </div>
            <div>
              <Label>Primary Position</Label>
              <Input value={form.position_primary} onChange={(e) => handleChange("position_primary", e.target.value)} data-testid="edit-position" />
            </div>
            <div>
              <Label>Secondary Position</Label>
              <Input value={form.position_secondary} onChange={(e) => handleChange("position_secondary", e.target.value)} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>School</Label>
                <VisibilityToggle field="school" value={visibility.school || "public"} onChange={handleVisibility} />
              </div>
              <Input value={form.school} onChange={(e) => handleChange("school", e.target.value)} data-testid="edit-school" />
              {isVerified("school") && <span className="text-xs text-green-600 flex items-center gap-1 mt-0.5"><ShieldCheck size={10} />NSB Verified</span>}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Location</Label>
                <VisibilityToggle field="location" value={visibility.location || "public"} onChange={handleVisibility} />
              </div>
              <Input value={form.location} onChange={(e) => handleChange("location", e.target.value)} data-testid="edit-location" />
            </div>
            <div>
              <Label>Graduation Year</Label>
              <Input value={form.graduation_year} onChange={(e) => handleChange("graduation_year", e.target.value)} data-testid="edit-grad-year" />
            </div>
          </div>

          {/* Physical */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>Height</Label>
                <VisibilityToggle field="height" value={visibility.height || "public"} onChange={handleVisibility} />
              </div>
              <Input value={form.height} onChange={(e) => handleChange("height", e.target.value)} placeholder="5'11&quot;" data-testid="edit-height" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Weight</Label>
                <VisibilityToggle field="weight" value={visibility.weight || "public"} onChange={handleVisibility} />
              </div>
              <Input value={form.weight} onChange={(e) => handleChange("weight", e.target.value)} placeholder="175 lbs" data-testid="edit-weight" />
            </div>
            <div>
              <Label>Bats</Label>
              <select value={form.bats} onChange={(e) => handleChange("bats", e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">--</option>
                <option value="R">Right</option>
                <option value="L">Left</option>
                <option value="S">Switch</option>
              </select>
            </div>
            <div>
              <Label>Throws</Label>
              <select value={form.throws} onChange={(e) => handleChange("throws", e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">--</option>
                <option value="R">Right</option>
                <option value="L">Left</option>
              </select>
            </div>
          </div>

          {/* Academic */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>GPA</Label>
                <VisibilityToggle field="gpa" value={visibility.gpa || "coaches"} onChange={handleVisibility} />
              </div>
              <Input value={form.gpa} onChange={(e) => handleChange("gpa", e.target.value)} placeholder="3.5" data-testid="edit-gpa" />
            </div>
            <div>
              <Label>Highlight Video URL</Label>
              <Input value={form.highlight_video_url} onChange={(e) => handleChange("highlight_video_url", e.target.value)} placeholder="https://..." data-testid="edit-video" />
            </div>
          </div>

          {/* Bio */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Bio</Label>
              <VisibilityToggle field="bio" value={visibility.bio || "public"} onChange={handleVisibility} />
            </div>
            <textarea
              value={form.bio}
              onChange={(e) => handleChange("bio", e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-none"
              placeholder="Tell coaches about yourself..."
              data-testid="edit-bio"
            />
          </div>

          {/* Visibility for data sections */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm text-blue-900">Section Visibility Controls</h4>
            <p className="text-xs text-blue-700">Choose who can see each section of your profile.</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Performance Metrics</span>
                <VisibilityToggle field="metrics" value={visibility.metrics || "public"} onChange={handleVisibility} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Scouting Reports</span>
                <VisibilityToggle field="scouting_reports" value={visibility.scouting_reports || "coaches"} onChange={handleVisibility} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Uploaded Files & Media</span>
                <VisibilityToggle field="files" value={visibility.files || "coaches"} onChange={handleVisibility} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Highlight Video</span>
                <VisibilityToggle field="highlight_video_url" value={visibility.highlight_video_url || "public"} onChange={handleVisibility} />
              </div>
            </div>
          </div>
        </form>

        <div className="flex gap-3 px-6 py-4 border-t justify-end">
          <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="profile-save-btn">
            <Save size={14} className="mr-1" />{saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
